import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { addMinutes, format, isValid, parseISO } from "date-fns";
// @ts-ignore
import { useTheme } from "@mui/material/styles";
import { useCookies } from "react-cookie";
// actions
import { useActions } from "../../../common/hooks/useActions";
import { frankfurtCardActionCreators } from "./frankfurtCardModule";
// components
import { TransportRowAction } from "../shared/transportRow";
import AppDatePickerCustom from "../../../components/AppDatePickerCustom";
import AppButton from "../../../components/ui/AppButton";
import AppBookingError from "../../../components/AppBookingError";
import TransportProviderHeader from "../../../components/TransportProviderContent/TransportProviderHeader";
import FrankfurtCardPassengersForm from "./FrankfurtCardPassengersForm";
import FrankfurtCardOfferSelection from "./FrankfurtCardOfferSelection";
import FrankfurtCardSummary from "./FrankfurtCardSummary";
import PaymentModal from "../../../components/Payment/PaymentModal";
// icons
import { dateIcon } from "../../../common/vector";
// hooks
import { useTransportLanguageRefresh } from "../../../common/hooks/useTransportLanguageRefresh";
// helpers
import { navigateBackOrToMain } from "../../../common/utils/helpers/navigateBackOrToMain";
import { pushTransportBookMarketplaceContext } from "../../../common/utils/analytics/pushTransportBookMarketplaceContext";
import {
  buildAirlineNavigationState,
  clearPersistedAirlineRouterState,
  persistAirlineFromNavigationState,
  resolveTransportAirlineApiContext
} from "../../../common/utils/transport/airlineTransportContext";
import { validateFrankfurtCardSeePrice } from "./helpers/validateFrankfurtCardSeePrice";
import { getPassengerFieldErrors } from "./helpers/getPassengerFieldErrors";
import { type FrankfurtCardOffer, type FrankfurtCardPassenger } from "./types";

export default function FrankfurtCard() {
  const theme = useTheme();
  const [cookies] = useCookies(["cookieAccepted"]);
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;
  const airlineOfferAppliedRef = useRef(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const language = localStorage.getItem("i18nextLng") || "en";

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [offerSelectionError, setOfferSelectionError] = useState(false);
  const [passengersRequiredError, setPassengersRequiredError] = useState(false);

  useLayoutEffect(() => {
    persistAirlineFromNavigationState(location.state);
  }, [location.state]);

  const restoredNavState = useMemo(() => buildAirlineNavigationState(location.state), [location.state]);

  const transportAirlineContext = useMemo(() => resolveTransportAirlineApiContext(location.state), [location.state]);

  const airlineNav = useMemo(() => {
    const nav = (restoredNavState ?? location.state) as {
      meta?: { source?: string };
      shared?: {
        offer_code?: string;
        departureDate?: string;
        arrivalDestination?: {
          placeId?: string | null;
          formattedAddress?: { secondary_text?: string | null } | null;
        } | null;
        finalDestination?: {
          placeId?: string | null;
          description?: string | null;
        } | null;
      };
    } | null;

    if (nav?.meta?.source !== "airline") {
      return {
        isAirlineSource: false,
        airlineOfferCode: undefined as string | undefined,
        airlineDepartureDate: undefined as string | undefined,
        departureAddress: null as string | null,
        departureAddressId: null as string | null,
        destinationAddress: null as string | null,
        destinationAddressId: null as string | null
      };
    }

    return {
      isAirlineSource: true,
      airlineOfferCode: nav.shared?.offer_code,
      airlineDepartureDate: nav.shared?.departureDate,
      departureAddress: nav.shared?.arrivalDestination?.formattedAddress?.secondary_text ?? null,
      departureAddressId: nav.shared?.arrivalDestination?.placeId ?? null,
      destinationAddress: nav.shared?.finalDestination?.description ?? null,
      destinationAddressId: nav.shared?.finalDestination?.placeId ?? null
    };
  }, [location.state, restoredNavState]);

  const {
    isAirlineSource,
    airlineOfferCode,
    airlineDepartureDate,
    departureAddress,
    departureAddressId,
    destinationAddress,
    destinationAddressId
  } = airlineNav;

  // states
  const tenantInfo = useSelector((state: any) => state.tenant.tenantInfo);
  const platformType = useSelector((state: any) => state.global.platformType);
  const globalLoader = useSelector((state: any) => state.global.globalLoader);
  const offers = useSelector((state: any) => state.frankfurtCard.offers);
  const selectedOffer = useSelector((state: any) => state.frankfurtCard.selectedOffer);
  const passengerList = useSelector((state: any) => state.frankfurtCard.passengerList) as FrankfurtCardPassenger[];
  const prebookingInfoResponse = useSelector((state: any) => state.frankfurtCard.prebookingInfoResponse);
  const prebookingError = useSelector((state: any) => state.frankfurtCard.prebookingError);
  const bookingError = useSelector((state: any) => state.frankfurtCard.bookingError);
  const bookingResponseData = useSelector((state: any) => state.frankfurtCard.bookingResponseData);
  const showPaymentModal = useSelector((state: any) => state.frankfurtCard.showPaymentModal);

  // actions
  const resetFrankfurtCardState = useActions(frankfurtCardActionCreators.resetFrankfurtCardStateAction, []);
  const getFrankfurtCardOffers = useActions(frankfurtCardActionCreators.getFrankfurtCardOffersAction, []);
  const setSelectedOffer = useActions(frankfurtCardActionCreators.setSelectedOfferAction, []);
  const updateShowValidationErrors = useActions(frankfurtCardActionCreators.updateShowValidationErrorsAction, []);
  const getPriceFrankfurtCardPrebook = useActions(frankfurtCardActionCreators.getPriceFrankfurtCardPrebookAction, []);
  const buyFrankfurtCardTicket = useActions(frankfurtCardActionCreators.buyFrankfurtCardTicketAction, []);
  const togglePaymentModal = useActions(frankfurtCardActionCreators.togglePaymentModalAction, []);
  const toggleBookingError = useActions(frankfurtCardActionCreators.toggleBookingErrorAction, []);

  useEffect(() => {
    return () => {
      resetFrankfurtCardState();
      clearPersistedAirlineRouterState();
    };
  }, [resetFrankfurtCardState]);

  useEffect(() => {
    // Airline extension: prefill starting date on initial load.
    if (!airlineDepartureDate) {
      return;
    }

    const parsed = parseISO(airlineDepartureDate);
    if (!isValid(parsed)) {
      return;
    }

    setSelectedDate(parsed);
  }, [airlineDepartureDate]);

  useEffect(() => {
    if (tenantInfo) {
      getFrankfurtCardOffers(transportAirlineContext);
    }
  }, [tenantInfo, getFrankfurtCardOffers, transportAirlineContext]);

  useTransportLanguageRefresh({
    language,
    navigate,
    pathSuffix: "/transport/frankfurt-card",
    getNavigationState: () => buildAirlineNavigationState(locationRef.current.state),
    iataCode: transportAirlineContext.mode === "airline" ? transportAirlineContext.iataCode : null
  });

  useEffect(() => {
    // Apply airline offer_code once when catalog is ready — never re-apply after the guest changes selection.
    if (airlineOfferAppliedRef.current || !airlineOfferCode || !offers?.items?.length) {
      return;
    }

    const matchedOffer = offers.items.find((offer: FrankfurtCardOffer) => offer?.offer_code === airlineOfferCode);
    if (!matchedOffer) {
      airlineOfferAppliedRef.current = true;
      return;
    }

    setSelectedOffer(matchedOffer);
    airlineOfferAppliedRef.current = true;
  }, [airlineOfferCode, offers, setSelectedOffer]);

  const getDisabledStyleForTicketsForm = () => {
    if (prebookingInfoResponse) {
      return { opacity: 0.4, pointerEvents: "none" as const };
    }
  };

  const passengerFieldErrors = useMemo(
    () => getPassengerFieldErrors(prebookingError, passengerList.length),
    [prebookingError, passengerList.length]
  );

  const renderPrebookErrorMsg = () => {
    if (!prebookingError) {
      return null;
    }

    const { byPassenger, other } = passengerFieldErrors;

    // 400 validation errors: passenger ones are shown under their inputs, only the rest is listed here.
    if (other.length > 0 || Object.keys(byPassenger).length > 0) {
      return other.map((fieldError, index) => (
        <div key={index} className="frankfurt-card__error-msg">
          {fieldError.errorMessage}
        </div>
      ));
    }

    return (
      <div className="frankfurt-card__error-msg">
        {prebookingError?.message ? prebookingError.message : t("transport.prebookCustomError")}
      </div>
    );
  };

  const handleSeePriceBtn = () => {
    updateShowValidationErrors(true);

    const validation = validateFrankfurtCardSeePrice({
      selectedOffer,
      selectedDate,
      passengerList
    });

    setOfferSelectionError(validation.offerMissing);
    setPassengersRequiredError(validation.passengersMissing);

    if (!validation.isValid) {
      return;
    }

    // Same +30 min offset as Munich City Pass: API expects a future datetime, but there is no time picker.
    const selectedDatePlus30min = addMinutes(selectedDate, 30);

    getPriceFrankfurtCardPrebook(
      {
        start_date: format(selectedDatePlus30min, "yyyy-MM-dd HH:mm"),
        offer_code: selectedOffer?.offer_code,
        departureAddress,
        departureAddressId,
        destinationAddress,
        destinationAddressId
      },
      transportAirlineContext
    );
  };

  const handleSelectedOffer = (offer: FrankfurtCardOffer) => {
    setSelectedOffer(offer);
    setOfferSelectionError(false);
  };

  const handleBuyTicketBtn = () => {
    pushTransportBookMarketplaceContext({
      cookieAccepted: cookies.cookieAccepted,
      platformType,
      tenantInfo,
      offerCode: selectedOffer?.offer_code ?? null
    });

    buyFrankfurtCardTicket(
      {
        selectedDate: Math.round(+new Date(selectedDate) / 1000),
        offer_code: selectedOffer?.offer_code ?? null
      },
      navigate,
      transportAirlineContext
    );
  };

  return (
    <Grid
      className="frankfurt-card grid-box"
      item
      sx={{ flexDirection: "column", flex: 1, margin: 0, padding: 0 }}
      mt={{ xs: 1, sm: 3 }}
      mb={{ xs: 4, sm: 5 }}
    >
      {bookingError ? (
        <AppBookingError
          dataAttribute="frankfurt-card-booking-error-button"
          onButtonClick={() => toggleBookingError(false)}
          title={t("bookingForm.error.title")}
          subtitle={t("bookingForm.error.message")}
        />
      ) : (
        <>
          <TransportProviderHeader
            title={offers?.provider_title ?? offers?.title}
            shortDescription={offers?.provider_description}
            providerLogo={offers?.provider_logo}
          />

          {offers?.items?.length > 0 ? (
            <Grid container>
              <Grid
                item
                sx={{ display: "flex", flexDirection: "column", ...getDisabledStyleForTicketsForm() }}
                md={6}
                xs={12}
              >
                <TransportRowAction
                  label={t("transport.frankfurtCard.startingDate")}
                  icon={dateIcon?.default}
                  actionComponent={
                    <Grid item width={{ xs: 200, sm: 220, md: 260 }}>
                      <AppDatePickerCustom
                        selectedDate={selectedDate}
                        setSelectedDate={date => date != null && setSelectedDate(date)}
                        minDate={new Date()}
                        dataAttribute="frankfurt-card-date"
                      />
                    </Grid>
                  }
                />
                {offers?.highlight ? (
                  <TransportRowAction
                    label={""}
                    actionComponent={
                      <Grid item width={{ xs: 200, sm: 220, md: 260 }}>
                        <Box
                          key={offers.highlight}
                          className="frankfurt-card__note"
                          data-cy="frankfurt-card-booking-note"
                          dangerouslySetInnerHTML={{ __html: offers.highlight }}
                        />
                      </Grid>
                    }
                  />
                ) : null}
              </Grid>
              <Grid
                item
                md={6}
                xs={12}
                mt={{ sm: 6, xs: 6, md: 0 }}
                sx={{ ...getDisabledStyleForTicketsForm() }}
                position={"relative"}
              >
                <div className="vertical-divider" />
                <Box pl={{ xs: 2, sm: 6 }} pr={{ xs: 2, sm: 4 }}>
                  <FrankfurtCardOfferSelection
                    offers={offers.items}
                    selectedOffer={selectedOffer}
                    onSelectOffer={handleSelectedOffer}
                  />
                  <FrankfurtCardPassengersForm />

                  {renderPrebookErrorMsg()}
                  {offerSelectionError && (
                    <div className="frankfurt-card__error-msg">{t("transport.frankfurtCard.offerRequired")}</div>
                  )}
                  {passengersRequiredError && (
                    <div className="frankfurt-card__error-msg">{t("transport.frankfurtCard.passengersRequired")}</div>
                  )}

                  <Grid
                    container={isAirlineSource}
                    item={!isAirlineSource}
                    xs={isAirlineSource ? undefined : 12}
                    sm={isAirlineSource ? undefined : 4}
                    sx={{
                      ...(isAirlineSource ? { display: "flex", alignItems: "stretch", gap: 2 } : {})
                    }}
                  >
                    {isAirlineSource && (
                      <Grid item sx={{ flex: 1 }}>
                        <AppButton
                          label={t("transport.goBack")}
                          onClick={() => navigateBackOrToMain(navigate, { language })}
                          disableRipple={true}
                          customBackgroundColor={theme.palette.primary.dark}
                          customHoverColor={theme.palette.primary.dark}
                          fontSize="14px"
                          dataAttribute="frankfurt-card-go-back-1"
                        />
                      </Grid>
                    )}

                    <Grid
                      item
                      sx={{
                        ...(isAirlineSource ? { flex: 1 } : {})
                      }}
                    >
                      <AppButton
                        label={t("transport.seePriceBtn")}
                        onClick={handleSeePriceBtn}
                        disableRipple={true}
                        customBackgroundColor={tenantInfo?.style?.button?.color}
                        customActiveColor={tenantInfo?.style?.button?.active}
                        customHoverColor={tenantInfo?.style?.button?.hover}
                        customClickColor={tenantInfo?.style?.button?.click}
                        fontSize="14px"
                        dataAttribute="frankfurt-card-see-price"
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12} mt={{ xs: 4, sm: 6 }} ml={{ xs: 2, sm: 5, md: 6 }} mr={{ xs: 2, sm: 5, md: 6 }}>
                <FrankfurtCardSummary
                  onBuyTicket={handleBuyTicketBtn}
                  selectedOffer={selectedOffer}
                  cancellationPolicy={selectedOffer?.cancellation_policy || offers?.cancellation_policy}
                />
              </Grid>
            </Grid>
          ) : (
            !globalLoader && (
              <Grid item xs={12} pb={4}>
                <Box sx={{ fontSize: 18 }}>{t("transport.notAvailable")}</Box>
              </Grid>
            )
          )}
        </>
      )}
      {showPaymentModal && (
        <PaymentModal
          isOpen={showPaymentModal}
          bookingResponseData={bookingResponseData}
          togglePaymentModal={togglePaymentModal}
          redirectUrl="transport/frankfurt-card/confirmation"
        />
      )}
    </Grid>
  );
}
